/**
 * Tipos para el módulo de Base de Conocimiento
 */

// Enums
export type TipoArticulo = 'TIP' | 'PROCEDIMIENTO' | 'FAQ' | 'MANUAL' | 'VIDEO';
export type AmbitoArticulo = 'SOPORTE_CLIENTE' | 'OPERATIVO_INTERNO' | 'ADMINISTRATIVO' | 'PERSONAL';
export type VisibilidadArticulo = 'PUBLICO' | 'CLIENTE_ESPECIFICO' | 'INTERNO';
export type EstadoArticulo = 'BORRADOR' | 'PUBLICADO' | 'ARCHIVADO';

// Labels para UI
export const TIPO_ARTICULO_LABELS: Record<TipoArticulo, string> = {
  TIP: 'Tip',
  PROCEDIMIENTO: 'Procedimiento',
  FAQ: 'FAQ',
  MANUAL: 'Manual',
  VIDEO: 'Video',
};

export const AMBITO_ARTICULO_LABELS: Record<AmbitoArticulo, string> = {
  SOPORTE_CLIENTE: 'Soporte Cliente',
  OPERATIVO_INTERNO: 'Operativo Interno',
  ADMINISTRATIVO: 'Administrativo',
  PERSONAL: 'Personal',
};

export const VISIBILIDAD_ARTICULO_LABELS: Record<VisibilidadArticulo, string> = {
  PUBLICO: 'Público',
  CLIENTE_ESPECIFICO: 'Cliente Específico',
  INTERNO: 'Interno',
};

export const ESTADO_ARTICULO_LABELS: Record<EstadoArticulo, string> = {
  BORRADOR: 'Borrador',
  PUBLICADO: 'Publicado',
  ARCHIVADO: 'Archivado',
};

// Colores para estados
export const ESTADO_ARTICULO_COLORS: Record<EstadoArticulo, string> = {
  BORRADOR: 'bg-yellow-100 text-yellow-800',
  PUBLICADO: 'bg-green-100 text-green-800',
  ARCHIVADO: 'bg-gray-100 text-gray-800',
};

export const TIPO_ARTICULO_COLORS: Record<TipoArticulo, string> = {
  TIP: 'bg-blue-100 text-blue-800',
  PROCEDIMIENTO: 'bg-purple-100 text-purple-800',
  FAQ: 'bg-cyan-100 text-cyan-800',
  MANUAL: 'bg-indigo-100 text-indigo-800',
  VIDEO: 'bg-pink-100 text-pink-800',
};

// Interfaces de Categoría
export interface CategoriaConocimiento {
  id: string;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  padre_id: string | null;
  orden: number;
  activo: boolean;
  created_at: string;
  updated_at: string | null;
  articulos_count: number;
}

export interface CategoriaArbol extends Omit<CategoriaConocimiento, 'articulos_count'> {
  hijos: CategoriaArbol[];
}

export interface CategoriaCreate {
  codigo: string;
  nombre: string;
  descripcion?: string;
  padre_id?: string;
  orden?: number;
}

export interface CategoriaUpdate {
  nombre?: string;
  descripcion?: string;
  padre_id?: string | null;
  orden?: number;
  activo?: boolean;
}

// Interfaces de Artículo
export interface UsuarioResumen {
  id: string;
  nombre: string;
}

export interface EtiquetaResumen {
  id: string;
  nombre: string;
  color: string;
}

export interface ArticuloListItem {
  id: string;
  numero: number;
  titulo: string;
  slug: string;
  tipo: TipoArticulo;
  ambito: AmbitoArticulo;
  estado: EstadoArticulo;
  categoria_id: string | null;
  categoria_nombre: string | null;
  vistas_totales: number;
  created_at: string;
}

export interface Articulo {
  id: string;
  numero: number;
  titulo: string;
  slug: string;
  tipo: TipoArticulo;
  ambito: AmbitoArticulo;
  visibilidad: VisibilidadArticulo;
  contenido: string | null;
  contenido_html: string | null;
  resumen: string | null;
  respuesta_corta: string | null;
  estado: EstadoArticulo;
  categoria_id: string | null;
  cliente_id: string | null;
  origen_ticket_id: string | null;
  origen_conversacion_id: string | null;
  creado_por_id: string;
  modulos_erp: string[] | null;
  version_actual: number;
  vistas_totales: number;
  veces_util: number;
  veces_no_util: number;
  usado_en_tickets: number;
  palabras_clave_auto: string[] | null;
  preguntas_frecuentes: string[] | null;
  nivel_complejidad: number | null;
  publicado_at: string | null;
  activo: boolean;
  created_at: string;
  updated_at: string;
  categoria: CategoriaConocimiento | null;
  etiquetas: EtiquetaResumen[];
  creado_por: UsuarioResumen | null;
}

export interface ArticuloCreate {
  titulo: string;
  tipo: TipoArticulo;
  ambito: AmbitoArticulo;
  visibilidad?: VisibilidadArticulo;
  contenido: string;
  resumen?: string;
  respuesta_corta?: string;
  categoria_id?: string;
  cliente_id?: string;
  etiqueta_ids?: string[];
  modulos_erp?: string[];
}

export interface ArticuloUpdate {
  titulo?: string;
  contenido?: string;
  resumen?: string;
  respuesta_corta?: string;
  tipo?: TipoArticulo;
  ambito?: AmbitoArticulo;
  visibilidad?: VisibilidadArticulo;
  categoria_id?: string | null;
  cliente_id?: string | null;
  etiqueta_ids?: string[];
  modulos_erp?: string[];
  motivo_cambio?: string;
}

// Interfaces de Versiones
export interface ArticuloVersion {
  id: string;
  articulo_id: string;
  version: number;
  titulo: string;
  motivo_cambio: string | null;
  modificado_por_id: string;
  modificado_por_nombre: string | null;
  created_at: string;
}

export interface ArticuloVersionDetalle extends ArticuloVersion {
  contenido: string;
  contenido_html: string | null;
}

// Filtros y búsqueda
export interface ArticuloFiltros {
  tipo?: TipoArticulo;
  ambito?: AmbitoArticulo;
  categoria_id?: string;
  estado?: EstadoArticulo;
  skip?: number;
  limit?: number;
}

export interface ArticuloBusquedaParams extends ArticuloFiltros {
  q: string;
}
